import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('jambo_token'));
  const [loading, setLoading] = useState(true);
  const fetchAttemptRef = useRef(0);
  const maxRetries = 2;

  const fetchUser = useCallback(async (currentToken) => {
    if (!currentToken) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        timeout: 10000 // 10 second timeout
      });
      setUser(response.data);
      fetchAttemptRef.current = 0; // Reset on success
    } catch (error) {
      console.error('Failed to fetch user:', error);
      
      // Only logout on auth errors (401/403), not network errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token is invalid/expired - clear session
        localStorage.removeItem('jambo_token');
        setToken(null);
        setUser(null);
      } else if (fetchAttemptRef.current < maxRetries) {
        // Network error - retry
        fetchAttemptRef.current += 1;
        console.log(`Retrying user fetch (attempt ${fetchAttemptRef.current}/${maxRetries})`);
        setTimeout(() => fetchUser(currentToken), 1000);
        return; // Don't set loading to false yet
      }
      // After max retries or auth error, keep token but clear user
      // User can still attempt actions that might refresh their session
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUser(token);
  }, [token, fetchUser]);

  const requestOTP = async (phone) => {
    const response = await axios.post(`${API_URL}/api/auth/request-otp`, { phone });
    return response.data;
  };

  const verifyOTP = async (phone, otp) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-otp`, { phone, otp });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('jambo_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (phone, name, email) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { phone, name, email });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('jambo_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('jambo_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (name, email) => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    if (email) params.append('email', email);
    
    const response = await axios.put(`${API_URL}/api/auth/profile?${params}`, null, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    requestOTP,
    verifyOTP,
    register,
    logout,
    updateProfile,
    refreshUser: () => fetchUser(token)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
